import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, StructureCategory } from '@ourbranch/lookups';

import { getDwellingHeatingOptionsForFO6, getFO6ConstructionTypeOptions } from 'common/helpers/farm-helpers';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { LabelTooltip } from 'core/components/label-tooltip';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import useStyles from './initial-risk-detail.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const occupancyUseTypeByStructureCategory = lookupsJson.farm.occupancyUseTypeByStructureCategory;
const buildingClassOptions = lookupsJson.farm.buildingClassOptions;

const FO6RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue, errors } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const has5004Alert = alerts.some((alert) => alert.code === 5004);
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (has5004Alert) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [has5004Alert]);

  // Structure categories depend on state, so we need to get them dynamically
  const structureCategories = lookupsJson.farm.structureCategoriesByState[values.state];
  const ctOptions = getFO6ConstructionTypeOptions(values.state);

  // Check if structure category is Barns/Outbuildings, Storage Grain, Poultry Building, or new structure types
  const isBarnOutbuilding = farmRisk?.building?.buildingType === StructureCategory.BarnsOutbuildings;
  const isStorageGrain = farmRisk?.building?.buildingType === StructureCategory.SilosGrainBins;
  const isPoultryBuildings = farmRisk?.building?.buildingType === StructureCategory.PoultryBuildings;
  const isHoopBuilding = farmRisk?.building?.buildingType === StructureCategory.HoopBuildings;
  const isConfinementBuilding = farmRisk?.building?.buildingType === StructureCategory.ConfinementBuildings;
  const isFencing = farmRisk?.building?.buildingType === StructureCategory.Fencing;
  const isDwellingContents = farmRisk?.building?.buildingType === StructureCategory.DwellingContents;

  // Auto-set proper defaults for structure-specific fields
  useEffect(() => {
    if (isHoopBuilding) {
      // Hoop Building: Set roof type to indicate included in construction
      if (!farmRisk?.building?.roofType) {
        setFieldValue(`${buildingPath}.roofType`, 'NONEH');
      }

      // Note: Hoop buildings automatically get 'HP' occupancy use in backend processing
    }
  }, [isHoopBuilding, farmRisk?.building?.roofType, setFieldValue, buildingPath]);

  // Use normal dwelling heating options for underwriting (FO-1/FO-2/FO-3 style)
  // These will be mapped to agricultural codes when sending to 360Value/Verisk
  const currentHeatingOptions = getDwellingHeatingOptionsForFO6();

  // Check field requirements based on occupancy type
  const isTowerOccupancy = farmRisk?.building?.occupancy === 'GHT';
  const shouldEnableDiameterHeight = isTowerOccupancy;
  const shouldEnableSqFt = farmRisk?.building?.occupancy && !isTowerOccupancy;

  return (
    <Grid container spacing={2}>
      {values.state && (
        <Field
          xs={12}
          type="select"
          id={`${buildingPath}.buildingType`}
          name={`${buildingPath}.buildingType`}
          mode="light"
          label="Structure Category*"
          options={structureCategories}
          onChange={(val) => {
            if (['PB', 'PI', 'PO'].includes(val)) {
              setFieldValue(`${buildingPath}.occupancy`, 'PCH');
            }
          }}
        />
      )}

      {isBarnOutbuilding && (
        <>
          {/* Row 1: Occupancy or use type + Year built in + Roof year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeByStructureCategory[farmRisk?.building?.buildingType]}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Total sqft + Quality + Roof type + Construction type */}
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={barnRtOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Row 3: Has open sides? + Building class type + Number of solid fuel burning... */}
          <Field
            xs={3}
            type="checkbox"
            id={`${buildingPath}.structureOpenSides`}
            name={`${buildingPath}.structureOpenSides`}
            mode="light"
            label="Has open sides?"
            className={classes.openSidesCheckbox}
            helperText={farmRisk?.building?.buildingClass === 1 ? 'Building Class 1 cannot have open sides' : undefined}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingClass`}
            name={`${buildingPath}.buildingClass`}
            mode="light"
            label="Building class type"
            options={buildingClassOptions}
            fast={false}
            error={!!getIn(errors, `${buildingPath}.buildingClass`)}
            helperText={getIn(errors, `${buildingPath}.buildingClass`) || undefined}
          />
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id="farmRisk.building.numSolidFuelBurningAppliances"
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          {/* Row 4: Primary heating + Heating units */}
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            fast={false}
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isStorageGrain && (
        <>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeByStructureCategory[farmRisk?.building?.buildingType]}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.diameter`}
            name={`${buildingPath}.diameter`}
            mode="light"
            label="Diameter (ft)"
            disabled={!shouldEnableDiameterHeight}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.height`}
            name={`${buildingPath}.height`}
            mode="light"
            label="Height (ft)"
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
            disabled={!shouldEnableSqFt}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.bushelCapacity`}
            name={`${buildingPath}.bushelCapacity`}
            mode="light"
            label="Bushel capacity"
          />
        </>
      )}

      {isPoultryBuildings && (
        <>
          {/* Row 1: Year built + Roof year + Total sqft */}
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Grid item xs={2} />
          {/* Row 2: Quality + Roof type */}
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={barnRtOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Row 3: Number of solid fuel burning... + Primary heating + Heating units */}
          <LabelTooltip
            label="Solid Fuel Burning Appliances"
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn solid fuels like wood, coal, or pellets. Common examples: fireplace, wood stove, pellet stove, wood-burning furnace, or coal stove. Gas, propane, oil, and electric appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isHoopBuilding && (
        <>
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={[{ id: 'HOOP', value: 'Hoop' }]}
            disabled={true}
            helperText="Roof type included in Hoop Building construction"
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Hoop Building Type field removed - occupancy is now automatically set to 'HP' */}
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isConfinementBuilding && (
        <>
          {/* Row 1: Occupancy or use type + Year built in + Roof year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeByStructureCategory[farmRisk?.building?.buildingType]}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Total sqft + Quality + Roof type */}
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={barnRtOptions}
          />
          {/* Row 3: Construction type + Has open sides? */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          <Grid item xs={4} />
          {/* Row 4: Number of solid fuel burning... + Primary heating + Heating units */}
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isFencing && (
        <>
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${farmRiskPath}.coverages.coverageE`}
            name={`${farmRiskPath}.coverages.coverageE`}
            mode="light"
            label="Coverage Limit"
            format={{
              allowNegative: false,
              decimalScale: 0,
              thousandSeparator: true,
              prefix: '$'
            }}
          />
        </>
      )}

      {isDwellingContents && (
        <>
          {/* Row 1: Residence Type + Year Built + Roof Year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.residenceType`}
            name={`${buildingPath}.residenceType`}
            mode="light"
            label="Residence type/usage"
            options={[
              { id: 'rental', value: 'Rental' },
              { id: 'seasonal', value: 'Seasonal' },
              { id: 'vacant', value: 'Vacant' }
            ]}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Square Footage + Solid Fuel Burning Appliances */}
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <LabelTooltip
            label="Solid Fuel Burning Appliances"
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn solid fuels like wood, coal, or pellets. Common examples: fireplace, wood stove, pellet stove, wood-burning furnace, or coal stove. Gas, propane, oil, and electric appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          {/* Row 3: Primary Heating + Heating Units */}
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
          {/* Coverage E - Dwelling Limit */}
          <Field
            xs={6}
            type="numeric"
            id={`${farmRiskPath}.coverages.coverageE`}
            name={`${farmRiskPath}.coverages.coverageE`}
            mode="light"
            label="Coverage E - Dwelling Limit"
            format={{
              allowNegative: false,
              decimalScale: 0,
              thousandSeparator: true,
              prefix: '$'
            }}
          />
        </>
      )}

      {/* Risk address automatically inherits from location */}

      {has5004Alert && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}
    </Grid>
  );
};

export default FO6RiskDetail;
